import { useEffect, useRef, useState, type KeyboardEvent } from 'react'
import {
  CalendarCheck,
  Crown,
  Image,
  KeyRound,
  Settings2,
  ShieldCheck,
  SlidersHorizontal,
  User,
} from 'lucide-react'
import type { TFunction } from 'i18next'
import type { LucideIcon } from 'lucide-react'
import type { DashboardSettingsTab } from '@features/dashboard'
import '../profile-layout.css'

const settingsTabGroups: ReadonlyArray<{
  readonly labelKey: string
  readonly tabs: ReadonlyArray<{
    readonly icon: LucideIcon
    readonly labelKey: string
    readonly value: DashboardSettingsTab
  }>
}> = [
  {
    labelKey: 'Account & security',
    tabs: [
      { icon: User, labelKey: 'Account', value: 'account' },
      { icon: Image, labelKey: 'Avatar', value: 'avatar' },
      { icon: KeyRound, labelKey: 'Change Password', value: 'change-password' },
      { icon: ShieldCheck, labelKey: 'Security', value: 'security' },
    ],
  },
  {
    labelKey: 'Preferences & learning',
    tabs: [
      { icon: Settings2, labelKey: 'Systems', value: 'systems' },
      { icon: SlidersHorizontal, labelKey: 'Personal settings', value: 'personal' },
      { icon: Crown, labelKey: 'Subscription', value: 'subscription' },
      { icon: CalendarCheck, labelKey: 'Study plan', value: 'study-plan' },
    ],
  },
]

const wideLayoutMediaQuery = '(min-width: 900px)'

interface SettingsTabsProps {
  readonly onChange: (value: DashboardSettingsTab) => void
  readonly t: TFunction<'translation'>
  readonly value: DashboardSettingsTab
}

export function SettingsTabs({ onChange, t, value }: SettingsTabsProps) {
  const wrapRef = useRef<HTMLDivElement>(null)
  const scrollerRef = useRef<HTMLDivElement>(null)
  const [isVerticalLayout, setIsVerticalLayout] = useState(
    () =>
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia(wideLayoutMediaQuery).matches,
  )

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return
    }

    const mediaQuery = window.matchMedia(wideLayoutMediaQuery)
    const handleChange = () => {
      setIsVerticalLayout(mediaQuery.matches)
    }

    handleChange()
    mediaQuery.addEventListener('change', handleChange)

    return () => {
      mediaQuery.removeEventListener('change', handleChange)
    }
  }, [])

  useEffect(() => {
    const scroller = scrollerRef.current
    const wrap = wrapRef.current

    if (!scroller || !wrap) {
      return
    }

    const updateScrollCues = () => {
      wrap.dataset.scrollStart = String(scroller.scrollLeft > 4)
      wrap.dataset.scrollEnd = String(
        scroller.scrollLeft + scroller.clientWidth < scroller.scrollWidth - 4,
      )
    }

    updateScrollCues()
    scroller.addEventListener('scroll', updateScrollCues, { passive: true })

    const observer =
      typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(updateScrollCues)
    observer?.observe(scroller)

    return () => {
      scroller.removeEventListener('scroll', updateScrollCues)
      observer?.disconnect()
    }
  }, [])

  const revealTab = (tabValue: DashboardSettingsTab) => {
    scrollerRef.current
      ?.querySelector(`[data-settings-tab='${tabValue}']`)
      ?.scrollIntoView({ block: 'nearest', inline: 'nearest' })
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (
      !['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(event.key)
    ) {
      return
    }

    const tabs = Array.from(event.currentTarget.querySelectorAll<HTMLButtonElement>('[role="tab"]'))
    const currentIndex = tabs.findIndex((tab) => tab === document.activeElement)
    const lastIndex = tabs.length - 1

    if (currentIndex < 0) {
      return
    }

    event.preventDefault()

    const nextIndex = (() => {
      if (event.key === 'Home') {
        return 0
      }

      if (event.key === 'End') {
        return lastIndex
      }

      if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
        return currentIndex === 0 ? lastIndex : currentIndex - 1
      }

      return currentIndex === lastIndex ? 0 : currentIndex + 1
    })()

    tabs[nextIndex]?.focus()
    tabs[nextIndex]?.click()
  }

  return (
    <div className="settingsTabsWrap" ref={wrapRef}>
      <div className="settingsTabsScroller" ref={scrollerRef}>
        <div
          aria-label={t('Settings')}
          aria-orientation={isVerticalLayout ? 'vertical' : 'horizontal'}
          className="settingsTabsList"
          onKeyDown={handleKeyDown}
          role="tablist"
        >
          {settingsTabGroups.map((group) => (
            <div className="settingsTabsGroup" key={group.labelKey}>
              <p aria-hidden="true" className="settingsTabsGroupLabel">
                {t(group.labelKey)}
              </p>
              {group.tabs.map((tab) => {
                const isSelected = value === tab.value
                const TabIcon = tab.icon

                return (
                  <button
                    aria-controls="settings-tab-panel"
                    aria-selected={isSelected}
                    className="settingsTab"
                    data-settings-tab={tab.value}
                    id={`settings-tab-${tab.value}`}
                    key={tab.value}
                    onClick={() => {
                      onChange(tab.value)
                      revealTab(tab.value)
                    }}
                    role="tab"
                    tabIndex={isSelected ? 0 : -1}
                    type="button"
                  >
                    <TabIcon aria-hidden="true" className="settingsTabIcon" size={17} strokeWidth={2.1} />
                    <span className="settingsTabLabel">{t(tab.labelKey)}</span>
                  </button>
                )
              })}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
